import { useEffect, useState } from "react";
import { View, Text, TextInput } from "react-native";
import { Building2 } from "lucide-react-native";
import {
  ActionBanner,
  Dialog,
  DialogIcon,
  DialogPrimaryButton,
  MoonFieldInput,
  MoonFormField,
  SoftSecondaryButton,
  TextActionButton,
} from "@louise/moon-ui";
import { useTranslations } from "@src/packages/i18n";
import {
  usePatient,
  useUpdatePatient,
} from "@src/packages/patients/hooks/usePatient";
import { EMBER } from "../../../../onboarding/theme";

const buildingIcon =
  require("../../../../assets/images/you-icons/building.png") as number;

type Props = {
  visible: boolean;
  onClose: () => void;
};

export default function EmployerBenefitsModal({ visible, onClose }: Props) {
  const t = useTranslations();
  const { data: patient } = usePatient();
  const updatePatient = useUpdatePatient();
  const [employerBenefit, setEmployerBenefit] = useState("");
  const [saveError, setSaveError] = useState(false);

  useEffect(() => {
    if (!visible || updatePatient.isPending) return;

    setEmployerBenefit(patient?.fertilityIntent?.employerBenefit ?? "");
    setSaveError(false);
  }, [
    patient?.fertilityIntent?.employerBenefit,
    updatePatient.isPending,
    visible,
  ]);

  const handleClose = () => {
    if (!updatePatient.isPending) onClose();
  };

  const handleSave = async () => {
    setSaveError(false);
    try {
      await updatePatient.mutateAsync({
        fertilityIntent: {
          // The API's fertility-intent upsert requires this value when the
          // relation does not yet exist. Preserve the patient's unrelated
          // fertility-intent data rather than changing medical history here.
          periodRegularity:
            patient?.fertilityIntent?.periodRegularity ?? "unsure",
          // Opening this form is the affirmative action. A user can still
          // explicitly record that they have no benefit below.
          hasEmployerBenefits: true,
          employerBenefit: employerBenefit.trim() || null,
        },
      });
      onClose();
    } catch {
      setSaveError(true);
    }
  };

  const handleNoBenefits = async () => {
    setSaveError(false);
    try {
      await updatePatient.mutateAsync({
        fertilityIntent: {
          periodRegularity:
            patient?.fertilityIntent?.periodRegularity ?? "unsure",
          hasEmployerBenefits: false,
          // An explicit No must clear a provider that may have been saved
          // earlier rather than leaving an ambiguous record behind.
          employerBenefit: null,
        },
      });
      onClose();
    } catch {
      setSaveError(true);
    }
  };

  return (
    <Dialog
      visible={visible}
      onClose={handleClose}
      layout="centered"
      dismissOnBackdropTap={!updatePatient.isPending}
      contentStyle={{
        paddingTop: 28,
        paddingHorizontal: 24,
        paddingBottom: 24,
      }}
    >
      <DialogIcon source={buildingIcon} width={52} height={62} />

      <Text
        className="font-dm-sans"
        style={{
          fontSize: 20,
          lineHeight: 25,
          fontWeight: "600",
          color: EMBER.colors.text,
          textAlign: "center",
          marginTop: 8,
          marginBottom: 6,
        }}
      >
        {t("you.coverage_benefits.employer_benefits_modal_title")}
      </Text>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 14,
          lineHeight: 20,
          color: EMBER.colors.textLight,
          textAlign: "center",
          marginBottom: 20,
        }}
      >
        {t("you.coverage_benefits.employer_benefits_modal_description")}
      </Text>

      <MoonFormField
        label={t("you.coverage_benefits.employer_benefits_provider_label")}
        labelHint={t("you.coverage_benefits.employer_benefits_question_hint")}
        labelIcon={Building2}
      >
        <MoonFieldInput>
          <TextInput
            value={employerBenefit}
            onChangeText={setEmployerBenefit}
            accessibilityLabel={t(
              "you.coverage_benefits.employer_benefits_provider_label",
            )}
            placeholder={t(
              "you.coverage_benefits.employer_benefits_provider_placeholder",
            )}
            placeholderTextColor={EMBER.colors.textFaint}
            className="font-dm-sans"
            style={{
              minHeight: 44,
              paddingVertical: 10,
              fontSize: 14,
              color: EMBER.colors.text,
            }}
          />
        </MoonFieldInput>
      </MoonFormField>

      {saveError ? (
        <View style={{ marginTop: 16 }}>
          <ActionBanner
            variant="important"
            message={t("you.coverage_benefits.employer_benefits_save_failed")}
          />
        </View>
      ) : null}

      <View style={{ flexDirection: "row", gap: 10, marginTop: 22 }}>
        <SoftSecondaryButton
          label={t("you.coverage_benefits.employer_benefits_cancel")}
          onPress={handleClose}
          disabled={updatePatient.isPending}
          flex={1}
        />
        <DialogPrimaryButton
          label={t("you.coverage_benefits.employer_benefits_save")}
          onPress={handleSave}
          loading={updatePatient.isPending}
          flex={1}
        />
      </View>
      <TextActionButton
        label={t("you.coverage_benefits.employer_benefits_none_action")}
        onPress={handleNoBenefits}
        disabled={updatePatient.isPending}
        color={EMBER.colors.textLight}
        style={{ alignSelf: "center", marginTop: 4 }}
      />
    </Dialog>
  );
}
